@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_cus

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.allFromTabularCommit
import org.example.project.service.GlobalManager
import org.example.project.service.InfrastrukturApi
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.overflow
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div

@Composable
fun CUSParkplatzAuskunft(art: String = org.example.project.service.currentSaveArt()) {
    val scope = rememberCoroutineScope()
    var daten by remember { mutableStateOf<List<All>>(emptyList()) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandort by remember { mutableStateOf("") }
    var parkplatzVon by remember { mutableStateOf("") }
    var parkplatzBis by remember { mutableStateOf("") }
    var strukturParkplaetze by remember { mutableStateOf<List<String>>(emptyList()) }
    var saveAuftrag by remember { mutableStateOf<List<Pair<String, List<String>>>?>(null) }
    var deleteAuftrag by remember { mutableStateOf<List<String>?>(null) }
    var generatorAuftrag by remember { mutableStateOf<All?>(null) }
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard")
    )

    fun parkplatzRowId(item: All): String =
        "${item.standort}:${item.id}"

    suspend fun loadParkplaetze() {
        daten = letzteFilter?.let { InfrastrukturApi.searchParkplaetze(it) }.orEmpty()
            .sortedWith(
                compareBy<All>(
                    { it.standort.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.id.toIntOrNull() ?: Int.MAX_VALUE },
                    { it.platz }
                )
            )
    }

    LaunchedEffect(art, refreshVersion) {
        standorte = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: 0 }
        loadParkplaetze()
    }

    LaunchedEffect(standorte) {
        if (selectedStandort.isNotBlank() && selectedStandort !in standorte.map { it.id }) {
            selectedStandort = ""
        }
    }

    LaunchedEffect(selectedStandort, standorte) {
        val standort = standorte.firstOrNull { it.id == selectedStandort }
        strukturParkplaetze = eingebetteterStandortGeneratorRange(
            standort?.parkplatzVon.orEmpty(),
            standort?.parkplatzBis.orEmpty(),
            1
        )
        parkplatzVon = ""
        parkplatzBis = ""
    }

    LaunchedEffect(saveAuftrag) {
        val rows = saveAuftrag ?: return@LaunchedEffect
        rows.forEach { (rowId, cols) ->
            val existing = daten.firstOrNull { parkplatzRowId(it) == rowId }
            val updated = allFromTabularCommit(
                rowId = rowId,
                cols = listOf(
                    cols.getOrElse(0) { existing?.standort.orEmpty() },
                    cols.getOrElse(1) { existing?.platz.orEmpty() },
                    cols.getOrElse(2) { if (existing?.belegt == true) "1" else "0" }
                ),
                fieldIds = listOf("standort", "platz", "belegt")
            )
            val final = updated.copy(
                id = existing?.id.orEmpty(),
                standort = updated.standort.ifBlank { selectedStandort },
                belegt = cols.getOrElse(2) { if (existing?.belegt == true) "✅" else "⬜" } == "✅",
                gesperrt = cols.getOrElse(3) { if (existing?.gesperrt == true) "✅" else "⬜" } == "✅"
            )
            if (final.standort.isBlank()) return@forEach
            val duplicate = daten.any {
                it.standort == final.standort &&
                    it.platz.equals(final.platz, ignoreCase = true) &&
                    parkplatzRowId(it) != rowId
            }
            if (duplicate) return@forEach
            if (existing != null && existing.standort != final.standort) {
                InfrastrukturApi.deleteParkplatz(existing.standort, existing.id)
            }
            InfrastrukturApi.saveParkplatz(final)
        }
        saveAuftrag = null
        loadParkplaetze()
    }

    LaunchedEffect(deleteAuftrag) {
        val ids = deleteAuftrag ?: return@LaunchedEffect
        ids.forEach { rowId ->
            val existing = daten.firstOrNull { parkplatzRowId(it) == rowId } ?: return@forEach
            InfrastrukturApi.deleteParkplatz(existing.standort, existing.id)
        }
        deleteAuftrag = null
        loadParkplaetze()
    }

    LaunchedEffect(generatorAuftrag) {
        val auftrag = generatorAuftrag ?: return@LaunchedEffect
        generatorAuftrag = null
        val standort = auftrag.standort.takeIf { it.isNotBlank() } ?: return@LaunchedEffect
        val von = auftrag.parkplatzVon.toIntOrNull() ?: return@LaunchedEffect
        val bis = auftrag.parkplatzBis.toIntOrNull() ?: von
        val existingNumbers = daten
            .filter { it.standort == standort }
            .mapNotNull { parkplatzNumber(it.platz) }
            .toSet()
        val neueParkplaetze = (von..bis)
            .filter { nr -> nr !in existingNumbers }
            .map { nr ->
                All(
                    id = nr.toString(),
                    standort = standort,
                    platz = "P$nr",
                    belegt = false,
                    gesperrt = false
                )
            }
        InfrastrukturApi.saveParkplaetze(neueParkplaetze)
        loadParkplaetze()
    }

    val standortOptionen = buildList {
        add(All(key = "", label = "Alle Standorte"))
        addAll(standorte.map { s ->
            All(key = s.id, label = s.standort.ifBlank { "Standort ${s.id}" })
        })
    }
    val sichtbareParkplaetze = daten.filter { selectedStandort.isBlank() || it.standort == selectedStandort }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(12.px)
            padding(12.px)
            height(100.percent)
            overflow("hidden")
            property("box-sizing", "border-box")
            property("min-height", "0")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                flexWrap(FlexWrap.Wrap)
                gap(8.px)
            }
        }) {
            StandortGeneratorDropdown(
                label = "Parkplatz von",
                selectedValue = parkplatzVon,
                options = strukturParkplaetze,
                enabled = selectedStandort.isNotBlank() && strukturParkplaetze.isNotEmpty(),
                onChange = { parkplatzVon = it }
            )
            StandortGeneratorDropdown(
                label = "Parkplatz bis",
                selectedValue = parkplatzBis,
                options = strukturParkplaetze,
                enabled = selectedStandort.isNotBlank() && strukturParkplaetze.isNotEmpty(),
                onChange = { parkplatzBis = it }
            )
            Div({ style { width(150.px) } }) {
                LumaticaButton(
                    label = "Generieren",
                    onClick = {
                        generatorAuftrag = All(
                            standort = selectedStandort,
                            parkplatzVon = parkplatzVon,
                            parkplatzBis = parkplatzBis
                        )
                    },
                    variant = LumaticaButtonVariant.Action,
                    enabled = selectedStandort.isNotBlank() && parkplatzVon.isNotBlank() && strukturParkplaetze.isNotEmpty()
                )
            }
        }

        Div({
            style {
                property("flex", "1 1 auto")
                property("min-height", "0")
                overflow("hidden")
            }
        }) {
            TabularScreen(
                screenKey = "ParkplatzAuskunft",
                rollenKey = "ParkplatzAuskunft",
                title = "Parkplatzauskunft",
                headers = listOf("Standort", "Parkplatz", "Belegt", "Gesperrt"),
                models = daten.map { it.copy(id = parkplatzRowId(it)) },
                data = sichtbareParkplaetze.map { parkplatz ->
                    parkplatzRowId(parkplatz) to listOf(
                        parkplatz.standort,
                        parkplatz.platz,
                        if (parkplatz.belegt) "1" else "0",
                        if (parkplatz.gesperrt == true) "✅" else "⬜"
                    )
                },
                editable = true,
                includeMetadata = true,
                toggleColumns = setOf("Belegt", "Gesperrt"),
                allowCreate = false,
                extraButtons = {
                    Div({ style { width(240.px) } }) {
                        AppDropdown(
                            options = standortOptionen,
                            selectedValue = selectedStandort,
                            onChange = { selectedStandort = it },
                            appearance = AppDropdownAppearance.Field,
                            menuMode = AppDropdownMenuMode.Overlay
                        )
                    }
                },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        letzteFilter = filters
                        daten = InfrastrukturApi.searchParkplaetze(filters)
                    }
                },
                onCommit = { rows ->
                    saveAuftrag = rows
                },
                onDeleteRows = { ids ->
                    val deletableIds = ids.filter { rowId ->
                        daten.firstOrNull { parkplatzRowId(it) == rowId }?.belegt != true
                    }
                    if (deletableIds.size != ids.size) {
                        error("Belegte Parkplätze können nicht gelöscht werden.")
                    }
                    deleteAuftrag = deletableIds
                }
            )
        }
    }
}

private fun parkplatzNumber(value: String): Int? =
    value.trim().removePrefix("P").removePrefix("p").toIntOrNull()
